import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { pprwAPI, taxonomyAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Search, Loader2, LayoutTemplate, Trash2, Pencil, Rocket, Globe } from 'lucide-react';
import { taxItems } from '../components/pprw/WorkshopFormDialog';

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];
const DELIVERY_TYPES = ['Live', 'Simu Live'];
const SOLUTION_AREAS = ['AI Business Solutions', 'Cloud and AI Platforms', 'Security'];
const fmtDate = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; } };

const Field = ({ label, children }) => (
  <div><label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>{children}</div>
);

function TemplateEditDialog({ open, onClose, template, categories, onSaved }) {
  const [form, setForm] = useState({ ...template });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.name?.trim()) { toast.error('Template name is required'); return; }
    setSaving(true);
    try {
      await pprwAPI.workshops.templates.update(template.id, {
        name: form.name, title: form.title, solution_area: form.solution_area, delivery_type: form.delivery_type,
        delivery_platform: form.delivery_platform, po_number: form.po_number, quarter: form.quarter,
        ms_owner: form.ms_owner, pmo: form.pmo,
      });
      toast.success('Template updated'); onSaved(); onClose();
    } catch (_e) { toast.error('Update failed'); } finally { setSaving(false); }
  };
  const platItems = taxItems(categories, 'delivery_platform');
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="template-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit Template</DialogTitle>
          <DialogDescription>Rename and adjust the common attributes this template applies to new workshops.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <Field label="Template Name *"><Input value={form.name || ''} onChange={e => set('name', e.target.value)} data-testid="template-edit-name" /></Field>
          <Field label="Workshop Title"><Input value={form.title || ''} onChange={e => set('title', e.target.value)} data-testid="template-edit-title" /></Field>
          <Field label="Solution Area">
            <Select value={form.solution_area || ''} onValueChange={v => set('solution_area', v)}>
              <SelectTrigger data-testid="template-edit-sa"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{SOLUTION_AREAS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Delivery Type">
            <Select value={form.delivery_type || ''} onValueChange={v => set('delivery_type', v)}>
              <SelectTrigger data-testid="template-edit-type"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{DELIVERY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Delivery Platform">
            {platItems.length ? (
              <Select value={form.delivery_platform || ''} onValueChange={v => set('delivery_platform', v)}>
                <SelectTrigger data-testid="template-edit-platform"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{platItems.map(i => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
              </Select>
            ) : <Input value={form.delivery_platform || ''} onChange={e => set('delivery_platform', e.target.value)} data-testid="template-edit-platform" />}
          </Field>
          <Field label="Quarter">
            <Select value={form.quarter || ''} onValueChange={v => set('quarter', v)}>
              <SelectTrigger data-testid="template-edit-quarter"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{QUARTERS.map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="MS Owner"><Input value={form.ms_owner || ''} onChange={e => set('ms_owner', e.target.value)} data-testid="template-edit-msowner" /></Field>
          <Field label="PMO"><Input value={form.pmo || ''} onChange={e => set('pmo', e.target.value)} data-testid="template-edit-pmo" /></Field>
          <Field label="PO Number"><Input value={form.po_number || ''} onChange={e => set('po_number', e.target.value)} data-testid="template-edit-po" /></Field>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="template-edit-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PPRWTemplateManagerPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setTemplates(await pprwAPI.workshops.templates.getAll()); }
    catch (_e) { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { taxonomyAPI.getAll().then(setCategories).catch(() => {}); }, []);

  const filtered = templates
    .filter(t => !search || (t.name || '').toLowerCase().includes(search.toLowerCase()) || (t.title || '').toLowerCase().includes(search.toLowerCase()) || (t.solution_area || '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => sortBy === 'name' ? (a.name || '').localeCompare(b.name || '') : (b.created_at || '').localeCompare(a.created_at || ''));

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    setBusy(id);
    try { await pprwAPI.workshops.templates.delete(id); toast.success('Template deleted'); load(); }
    catch { toast.error('Delete failed'); } finally { setBusy(null); }
  };

  const handleUse = async (t) => {
    setBusy(t.id);
    try {
      const w = await pprwAPI.workshops.templates.instantiate(t.id, {});
      toast.success(`Workshop created with ${w.session_count} session${w.session_count !== 1 ? 's' : ''}`);
      navigate(`/pprw/workshops/${w.id}`);
    } catch (_e) { toast.error('Failed to create workshop'); setBusy(null); }
  };

  return (
    <div className="p-6 space-y-5" data-testid="template-manager-page">
      <button onClick={() => navigate('/pprw')} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="template-manager-back">
        <ArrowLeft className="w-4 h-4" />Back to 1:Many PPRW
      </button>
      <div className="flex items-center gap-2">
        <LayoutTemplate className="w-5 h-5 text-lime" />
        <div>
          <h1 className="text-2xl font-bold">Workshop Template Library</h1>
          <p className="text-sm text-muted-foreground">View, rename and organise your reusable workshop templates</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-52">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search templates…" className="pl-9" data-testid="template-manager-search" />
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-40" data-testid="template-sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="name">Sort: Name</SelectItem>
            <SelectItem value="created">Sort: Newest</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <LayoutTemplate className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No templates yet. Open a workshop and click “Save as Template”.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden" data-testid="template-list">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>{['Template', 'Solution Area', 'Platform', 'Type', 'Time zones', 'Created', ''].map(h => (
                <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.id} className="border-t border-border hover:bg-muted/30 group" data-testid={`template-row-${t.id}`}>
                  <td className="px-3 py-3 max-w-[240px]">
                    <p className="font-semibold truncate">{t.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{t.title || '—'}</p>
                  </td>
                  <td className="px-3 py-3 text-xs">{t.solution_area || '—'}</td>
                  <td className="px-3 py-3 text-xs">{t.delivery_platform || '—'}</td>
                  <td className="px-3 py-3 text-xs">{t.delivery_type || '—'}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1 flex-wrap">
                      <Globe className="w-3 h-3 text-muted-foreground" />
                      {(t.slots || []).length === 0 ? <span className="text-[11px] text-muted-foreground">—</span> :
                        (t.slots || []).map((s, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{s.time_zone || 'TZ'}</span>)}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-xs whitespace-nowrap">{fmtDate(t.created_at)}</td>
                  <td className="px-3 py-3">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => handleUse(t)} disabled={busy === t.id} data-testid={`template-use-${t.id}`}>
                        {busy === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}Use
                      </Button>
                      <button className="p-1.5 rounded hover:bg-muted" title="Edit / rename" onClick={() => setEditing(t)} data-testid={`template-edit-${t.id}`}>
                        <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                      <button className="p-1.5 rounded hover:bg-red-50" title="Delete" onClick={() => handleDelete(t.id)} disabled={busy === t.id} data-testid={`template-delete-${t.id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <TemplateEditDialog open={!!editing} onClose={() => setEditing(null)} template={editing} categories={categories} onSaved={load} />}
    </div>
  );
}

export default PPRWTemplateManagerPage;
